// ============================================================================
// src/broiler/BroilerEntry.tsx — the broiler domain's nested routes.
// ============================================================================
// B3.1 finish, fourth domain. Replaces resolveBroilerEntry. See
// src/cattle/CattleEntry.jsx for the pattern and
// src/app/navigation/viewRoutes.js for why `location` is the ROUTED location.
//
// The `list` route still receives the broiler batch-form closures (upd,
// closeForm, submit, parseProcessorXlsx, openAdd, openEdit, persist, del)
// because those still live in App(). Moving that cluster here is B2.3r, the
// remaining half of B2.3; this file is where it lands. Until then the props
// are threaded exactly as the resolver threaded them -- BroilerListRouter
// forwards them to BroilerBatchPage → BatchForm, and without them the record
// page's fields are read-only.
// ============================================================================
import React from 'react';
import type {Dispatch, SetStateAction} from 'react';

import {AppRoute, AppRoutes} from '../app/navigation/index.js';
import {BROILER_ROUTES} from './broilerRoutes.js';

import BroilerHomeView from './BroilerHomeView.tsx';
import BroilerTimelineView from './BroilerTimelineView.jsx';
import BroilerDailysView from './BroilerDailysView.tsx';
import BroilerListView from './BroilerListView.tsx';
import BroilerFeedView from './BroilerFeedView.tsx';
import PageBoundary from '../app/PageBoundary.tsx';

export {BROILER_ROUTES};

// Declared here rather than shared with the other four domain entries: they
// have the same shape, not the same module, and a cross-domain import would add
// an edge to the graph the route guards walk.
type RoutedLocationProp = string | {pathname: string; search?: string; hash?: string; state?: unknown};
type RefreshDailys = (kind: string) => Promise<void>;

// ppp-v4 broiler batch rows. Their shape lives in the persisted app_store blob,
// not in any declaration this repo owns, and this component only forwards them.
type BatchList = unknown[];

// ppp-feed-orders-v1: App seeds it with {pig, starter, grower, layerfeed}, and
// BroilerFeedView indexes the second level by 'YYYY-MM'. The leaf values come
// straight out of the blob, so they stay unknown.
//
// THE VALUE IS NO LONGER FORWARDED, AND THE TYPE STILL DECLARES IT. /broiler/feed
// reads both blobs through broiler.feed.core now, so neither reaches
// BroilerFeedView as a prop — but App still passes them to this component, and
// App is not this change's to edit. Dropping either member from the props type
// would make that `React.createElement(BroilerEntry, {…})` call an excess-property
// error. They stay declared, and deliberately undestructured below.
type FeedOrders = Record<string, Record<string, unknown>>;

// ppp-poultry-feed-inventory-v1: feed type -> on-hand snapshot. App initialises
// the state to null, so null is a real value here, not a placeholder. Same
// disposition as FeedOrders above: declared, received, not forwarded.
type PoultryFeedInventory = Record<string, unknown> | null;

type BroilerEntryProps = {
  location: RoutedLocationProp;
  // STILL RECEIVED, DELIBERATELY NOT THREADED. App's dispatch hands this array
  // to every domain entry and src/App.tsx is not this lane's to change; the
  // route that used to consume it — /broiler/dailys/* — reads the same blob
  // through broiler.dailys.batchDirectory now. It is left off the destructure
  // below rather than forwarded, because forwarding it to any route would put
  // App's boot array back in the consumption the former-eager derivation
  // measures.
  batches: BatchList;
  refreshDailys: RefreshDailys;
  openAdd: () => void;
  // The batch row to load into the form. Same unknown element type as `batches`.
  openEdit: (batch: unknown) => void;
  // Takes the fully-built next batch array, never a React updater callback.
  persist: (next: BatchList) => Promise<boolean>;
  // The document save, narrowed to the row it edits. It takes an id and a
  // document list rather than an array, which is what lets BatchForm hold no
  // batch array at all — see src/App.tsx's persistDocuments. Threaded to the
  // batches route only: BroilerListRouter forwards the whole prop object to
  // BroilerBatchPage, which mounts the form embedded.
  persistDocuments: (batchId: string | null, documents: unknown[]) => Promise<boolean>;
  del: (id: string, onDeleted?: () => void) => void;
  // Role predicate, NOT a permission boolean: BroilerListView calls it as
  // canDeleteAnything(authState?.role), which can be undefined.
  canDeleteAnything: (role: string | undefined) => boolean;
  // Broiler batch-form field write. `value` is whatever the control produced.
  upd: (key: string, value: unknown) => void;
  // Returns false when the form refuses to close on unsaved changes —
  // BroilerBatchPage checks the result before navigating away.
  closeForm: () => Promise<boolean>;
  // `force` bypasses hard conflicts; the boolean says whether the save ran.
  submit: (force: boolean) => Promise<boolean>;
  parseProcessorXlsx: (file: File) => Promise<void>;
  feedOrders: FeedOrders;
  setFeedOrders: Dispatch<SetStateAction<FeedOrders>>;
  poultryFeedInventory: PoultryFeedInventory;
  setPoultryFeedInventory: Dispatch<SetStateAction<PoultryFeedInventory>>;
  // Collapsed-section keys ('layers', batch names). BroilerFeedView passes an
  // updater, so this is the raw state setter.
  collapsedBatches: Set<string>;
  setCollapsedBatches: Dispatch<SetStateAction<Set<string>>>;
};

export function BroilerEntry({
  location,
  refreshDailys,
  openAdd,
  openEdit,
  persist,
  persistDocuments,
  del,
  canDeleteAnything,
  upd,
  closeForm,
  submit,
  parseProcessorXlsx,
}: BroilerEntryProps) {
  const routes = (
    <AppRoutes location={location}>
      <AppRoute path="/broiler" element={<BroilerHomeView />} />
      <AppRoute
        path="/broiler/timeline"
        element={
          <BroilerTimelineView
            openEdit={openEdit}
            openAdd={openAdd}
            upd={upd}
            closeForm={closeForm}
            submit={submit}
            del={del}
            parseProcessorXlsx={parseProcessorXlsx}
            persistDocuments={persistDocuments}
          />
        }
      />
      {/* No `batches` any more. Both /broiler/dailys surfaces read the batch
          directory through broiler.dailys.batchDirectory, mounted in
          BroilerDailysRouter above the index/record branch — which is exactly
          where this hand-off used to land. Handing the array down here would
          restore the exact consumption
          tests/static/former_eager_evidence_static.test.js derives, and make
          the `broilerdailys` retirement a false claim. */}
      <AppRoute path="/broiler/dailys/*" element={<BroilerDailysView refreshDailys={refreshDailys} />} />
      <AppRoute
        path="/broiler/batches/*"
        element={
          <BroilerListView
            openAdd={openAdd}
            openEdit={openEdit}
            persist={persist}
            persistDocuments={persistDocuments}
            del={del}
            canDeleteAnything={canDeleteAnything}
            upd={upd}
            closeForm={closeForm}
            submit={submit}
            parseProcessorXlsx={parseProcessorXlsx}
          />
        }
      />
      {/* /broiler/feed owns its route data, writes, and collapse-only UI state.
          App still supplies historical fields to BroilerEntry for now, but none
          crosses this route boundary. */}
      <AppRoute path="/broiler/feed" element={<BroilerFeedView />} />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default BroilerEntry;
